import { useRef, useState, useEffect } from "react";
import { Pen, Minus, Eraser, Mic } from "lucide-react";
import { api, formatApiError } from "../lib/api";

const fmt = (t) => `${Math.floor(t / 60)}:${String(Math.floor(t % 60)).padStart(2, "0")}`;

const MIME_CANDIDATES = [
  "video/webm;codecs=vp9,opus",
  "video/webm;codecs=vp8,opus",
  "video/webm",
];
const pickMime = () =>
  (typeof MediaRecorder !== "undefined" &&
    MIME_CANDIDATES.find((m) => MediaRecorder.isTypeSupported(m))) || "";

function paint(ctx, strokes, temp, w, h) {
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  const all = temp ? [...strokes, temp] : strokes;
  for (const s of all) {
    ctx.strokeStyle = s.color || "#C9A227";
    ctx.lineWidth = Math.max(2.5, w * 0.005);
    ctx.beginPath();
    if (s.type === "line") {
      ctx.moveTo(s.x1 * w, s.y1 * h);
      ctx.lineTo(s.x2 * w, s.y2 * h);
    } else if (s.pts && s.pts.length) {
      ctx.moveTo(s.pts[0].x * w, s.pts[0].y * h);
      for (let i = 1; i < s.pts.length; i++) ctx.lineTo(s.pts[i].x * w, s.pts[i].y * h);
    }
    ctx.stroke();
  }
}

export function CoachStudio({ videoId, videoUrl, onDone, onCancel }) {
  const vRef = useRef(null);
  const overlayRef = useRef(null);
  const captureRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const micRef = useRef(null);
  const strokesRef = useRef([]);
  const tempRef = useRef(null);
  const recordingRef = useRef(false);

  const [tool, setTool] = useState("pen");
  const [strokeCount, setStrokeCount] = useState(0);
  const [recording, setRecording] = useState(false);
  const [recordedUrl, setRecordedUrl] = useState(null);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [cur, setCur] = useState(0);
  const [dur, setDur] = useState(0);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    let raf;
    const loop = () => {
      const oc = overlayRef.current, v = vRef.current;
      if (oc) {
        const w = (oc.width = oc.clientWidth), h = (oc.height = oc.clientHeight);
        const ctx = oc.getContext("2d");
        ctx.clearRect(0, 0, w, h);
        paint(ctx, strokesRef.current, tempRef.current, w, h);
      }
      if (recordingRef.current && v && v.videoWidth) {
        const cc = captureRef.current;
        const ctx = cc.getContext("2d");
        ctx.drawImage(v, 0, 0, cc.width, cc.height);
        paint(ctx, strokesRef.current, tempRef.current, cc.width, cc.height);
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, []);

  useEffect(() => () => {
    micRef.current?.getTracks().forEach((t) => t.stop());
    if (recordedUrl) URL.revokeObjectURL(recordedUrl);
  }, [recordedUrl]);

  const norm = (e) => {
    const r = overlayRef.current.getBoundingClientRect();
    return { x: (e.clientX - r.left) / r.width, y: (e.clientY - r.top) / r.height };
  };
  const startDraw = (e) => {
    overlayRef.current.setPointerCapture?.(e.pointerId);
    const p = norm(e);
    tempRef.current = tool === "line"
      ? { type: "line", x1: p.x, y1: p.y, x2: p.x, y2: p.y, color: "#C9A227" }
      : { type: "pen", pts: [p], color: "#C9A227" };
  };
  const moveDraw = (e) => {
    const t = tempRef.current;
    if (!t) return;
    const p = norm(e);
    if (t.type === "line") { t.x2 = p.x; t.y2 = p.y; }
    else { t.pts.push(p); }
  };
  const endDraw = () => {
    if (!tempRef.current) return;
    strokesRef.current = [...strokesRef.current, tempRef.current];
    tempRef.current = null;
    setStrokeCount(strokesRef.current.length);
  };
  const clearDrawings = () => { strokesRef.current = []; tempRef.current = null; setStrokeCount(0); };

  const togglePlay = () => {
    const v = vRef.current;
    if (!v) return;
    if (v.paused) v.play().catch(() => {}); else v.pause();
  };
  const onMeta = () => {
    const v = vRef.current, cc = captureRef.current;
    setDur(v.duration || 0);
    if (cc && v.videoWidth) { cc.width = v.videoWidth; cc.height = v.videoHeight; }
  };
  const seek = (e) => { if (vRef.current) vRef.current.currentTime = Number(e.target.value); };

  const startRec = async () => {
    setErr("");
    const v = vRef.current, cc = captureRef.current;
    if (!v || !v.videoWidth) { setErr("Video is still loading — give it a second."); return; }
    cc.width = v.videoWidth; cc.height = v.videoHeight;
    cc.getContext("2d").drawImage(v, 0, 0, cc.width, cc.height);
    let mic;
    try {
      mic = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      setErr("Microphone access denied or unavailable.");
      return;
    }
    let cstream;
    try {
      cstream = cc.captureStream(30);
    } catch {
      setErr("Live recording isn't supported in this browser. Use desktop Chrome.");
      mic.getTracks().forEach((t) => t.stop());
      return;
    }
    const mixed = new MediaStream([...cstream.getVideoTracks(), ...mic.getAudioTracks()]);
    const mime = pickMime();
    let mr;
    try {
      mr = new MediaRecorder(mixed, mime ? { mimeType: mime } : undefined);
    } catch {
      setErr("Recording not supported in this browser.");
      mic.getTracks().forEach((t) => t.stop());
      return;
    }
    chunksRef.current = [];
    mr.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
    mr.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: "video/webm" });
      setRecordedBlob(blob);
      setRecordedUrl(URL.createObjectURL(blob));
      mic.getTracks().forEach((t) => t.stop());
      cstream.getTracks().forEach((t) => t.stop());
    };
    micRef.current = mic;
    recorderRef.current = mr;
    mr.start();
    recordingRef.current = true;
    setRecording(true);
    try { v.currentTime = 0; } catch { /* ignore */ }
    v.play().catch(() => {});
  };

  const stopRec = () => {
    recorderRef.current?.stop();
    recordingRef.current = false;
    setRecording(false);
    vRef.current?.pause();
  };

  const reRecord = () => {
    if (recordedUrl) URL.revokeObjectURL(recordedUrl);
    setRecordedUrl(null);
    setRecordedBlob(null);
    setErr("");
  };

  const send = async () => {
    if (!recordedBlob) { setErr("Record your response first."); return; }
    setSending(true); setErr("");
    try {
      const fd = new FormData();
      fd.append("text", text);
      fd.append("recording", recordedBlob, "response.webm");
      const { data } = await api.post(`/videos/${videoId}/response`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      onDone(data.coach_response);
    } catch (e) {
      setErr(formatApiError(e));
      setSending(false);
    }
  };

  const ToolBtn = ({ id, icon: Icon, label }) => (
    <button onClick={() => setTool(id)} data-testid={`studio-tool-${id}`}
            className={`btn-ghost !py-2 !px-3 text-xs flex items-center gap-1.5 ${tool === id ? "!border-[#C9A227] !text-[#C9A227]" : ""}`}>
      <Icon size={14} /> {label}
    </button>
  );

  return (
    <div data-testid="coach-studio" className="border border-[#C9A227]/40 bg-white/[0.02] p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="overline" style={{ color: "#C9A227" }}>Record Session</div>
        <button onClick={onCancel} data-testid="studio-cancel" className="text-white/50 hover:text-white text-sm">Close</button>
      </div>

      <canvas ref={captureRef} className="hidden" />

      {recordedUrl ? (
        <div data-testid="studio-preview">
          <div className="overline mb-2" style={{ color: "#A0B0A8" }}>Your recorded breakdown</div>
          <div className="bg-black aspect-video border border-[#C9A227]/40 overflow-hidden">
            <video src={recordedUrl} controls playsInline className="w-full h-full object-contain" data-testid="studio-preview-video" />
          </div>
          <button onClick={reRecord} data-testid="studio-rerecord" className="btn-ghost !py-2 !px-4 text-xs mt-3">↺ Re-record</button>
        </div>
      ) : (
        <>
          <div className="relative bg-black aspect-video border border-white/10 overflow-hidden">
            <video ref={vRef} src={videoUrl} playsInline preload="auto"
                   onLoadedMetadata={onMeta}
                   onTimeUpdate={() => setCur(vRef.current?.currentTime || 0)}
                   onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)}
                   className="w-full h-full object-contain" />
            <canvas ref={overlayRef} data-testid="studio-canvas"
                    onPointerDown={startDraw} onPointerMove={moveDraw} onPointerUp={endDraw} onPointerLeave={endDraw}
                    className="absolute inset-0 w-full h-full cursor-crosshair touch-none" />
            {recording && (
              <div className="absolute top-2 left-2 flex items-center gap-2 px-2 py-1 bg-red-600 text-white text-xs font-bold rounded-sm">
                <span className="w-2 h-2 rounded-full bg-white animate-pulse" /> REC
              </div>
            )}
          </div>

          {/* Playback controls */}
          <div className="flex items-center gap-3 mt-3">
            <button onClick={togglePlay} data-testid="studio-playpause" className="btn-ghost !py-2 !px-4 text-xs w-20">
              {playing ? "❚❚ Pause" : "▶ Play"}
            </button>
            <input type="range" min={0} max={dur || 0} step={0.05} value={cur} onChange={seek}
                   data-testid="studio-seek" className="flex-1 accent-[#C9A227]" />
            <span className="text-white/50 text-xs tabular-nums w-20 text-right">{fmt(cur)} / {fmt(dur)}</span>
          </div>

          {/* Drawing tools */}
          <div className="flex flex-wrap items-center gap-2 mt-4">
            <ToolBtn id="pen" icon={Pen} label="Pen" />
            <ToolBtn id="line" icon={Minus} label="Line" />
            <button onClick={clearDrawings} data-testid="studio-clear" disabled={!strokeCount}
                    className="btn-ghost !py-2 !px-3 text-xs flex items-center gap-1.5 disabled:opacity-40">
              <Eraser size={14} /> Clear
            </button>
            <span className="text-white/40 text-xs self-center ml-2">Press Record, then play, talk and draw on the swing live.</span>
          </div>

          {/* Record control */}
          <div className="mt-5">
            {!recording ? (
              <button onClick={startRec} data-testid="studio-rec-start" className="btn-primary w-full flex items-center justify-center gap-2">
                <Mic size={16} /> Start Recording
              </button>
            ) : (
              <button onClick={stopRec} data-testid="studio-rec-stop" className="btn-primary w-full bg-red-600 hover:bg-red-700 flex items-center justify-center gap-2 animate-pulse">
                ■ Stop Recording
              </button>
            )}
          </div>
        </>
      )}

      {/* Written feedback */}
      <div className="mt-6">
        <div className="overline" style={{ color: "#A0B0A8" }}>Written summary (optional)</div>
        <textarea data-testid="studio-text" value={text} onChange={(e) => setText(e.target.value)} rows={3}
                  className="input-dark mt-2" placeholder="Key swing thoughts, drills, what to work on..." />
      </div>

      {err && <div className="text-red-400 text-sm mt-3" data-testid="studio-error">{err}</div>}

      <button onClick={send} disabled={sending || !recordedBlob} data-testid="studio-send"
              className="btn-primary w-full mt-5 disabled:opacity-50 disabled:cursor-not-allowed">
        {sending ? "Sending..." : "Send Response to Client"}
      </button>
    </div>
  );
}
